import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import Seo from "@/components/common/Seo";
import { CONTACT } from "@/data/site";

const UPDATED = "June 25, 2026";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: [
      `Reverence Autos ("Reverence Autos", "we", "us", or "our") is a mobile car detailing company operating across Ottawa, Ontario, Canada. This Privacy Policy explains how we collect, use, disclose and protect your personal information when you use our website and services.`,
      `We are committed to handling your information in accordance with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA).`,
    ],
  },
  {
    title: "2. Information we collect",
    list: [
      "Contact details you provide — your name, email address, phone number and service address when you book a detail, create an account, subscribe to our list, or contact us.",
      "Account information — your login credentials, date of birth (optional, used only to offer a birthday-month wash), membership status and referral code.",
      "Booking & vehicle details — the services you request, your vehicle type, preferred dates and any notes you share.",
      "Payment information — membership and service payments are processed by our payment provider (Stripe). We do not store your full card number; Stripe handles card data securely on our behalf.",
      "Support & feedback — messages, complaints or reviews you send us.",
      "Usage information — basic technical data such as your browser type and pages visited, used to keep the site working and secure.",
    ],
  },
  {
    title: "3. How we use your information",
    list: [
      "To schedule, confirm and deliver your detailing appointments, including sending confirmation and reminder text messages with an add-to-calendar link.",
      "To process payments and administer your membership, discounts and referral rewards.",
      "To respond to your enquiries, feedback and complaints, and to make things right when a service falls short.",
      "To send you service-related updates and, where you've opted in, marketing about specials and member perks (you can unsubscribe anytime).",
      "To operate, secure and improve our website and services.",
    ],
  },
  {
    title: "4. Text messages (SMS)",
    body: [
      `When you book with us, we send transactional text messages (booking confirmations and reminders) to the phone number you provide, using our messaging provider (Twilio). Message and data rates may apply. These messages are part of the service you request; you can ask us to stop by contacting us at ${CONTACT.email}.`,
    ],
  },
  {
    title: "5. How we share information",
    body: [
      "We do not sell your personal information. We share it only with trusted service providers who help us run our business, and only to the extent needed:",
    ],
    list: [
      "Stripe — to process payments securely.",
      "Twilio — to send you booking texts.",
      "Our booking and analytics infrastructure — to store your account and appointment details.",
      "Legal authorities — if required by law or to protect our rights, safety, or property.",
    ],
  },
  {
    title: "6. Data retention",
    body: [
      "We keep your personal information only for as long as necessary to provide our services, meet our legal and accounting obligations, and resolve disputes. When it is no longer needed, we securely delete or anonymize it.",
    ],
  },
  {
    title: "7. Your rights",
    body: [
      "Under Canadian privacy law you may request access to the personal information we hold about you, ask us to correct it, or ask us to delete it. You may also withdraw your consent to marketing communications at any time. To exercise any of these rights, contact us using the details below and we'll respond within a reasonable timeframe.",
    ],
  },
  {
    title: "8. Security",
    body: [
      "We use reasonable administrative, technical and physical safeguards to protect your information, including encrypted connections and reputable third-party providers for payments and messaging. No method of transmission over the internet is completely secure, but we work hard to protect your data.",
    ],
  },
  {
    title: "9. Cookies",
    body: [
      "Our website uses essential cookies and similar technologies to keep you signed in and to keep the site working properly. We do not use them to build advertising profiles.",
    ],
  },
  {
    title: "10. Children's privacy",
    body: [
      "Our services are intended for adults. We do not knowingly collect personal information from children under the age of majority in Ontario.",
    ],
  },
  {
    title: "11. Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time. When we do, we'll revise the \"Last updated\" date above. Significant changes will be highlighted on this page.",
    ],
  },
  {
    title: "12. Contact us",
    body: [
      `If you have any questions about this Privacy Policy or how we handle your information, please reach out:`,
    ],
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo
        title="Privacy Policy | Reverence Autos"
        description="How Reverence Autos collects, uses and protects your personal information — in line with Canada's PIPEDA."
        path="/privacy"
      />

      <section className="relative overflow-hidden bg-brand-charcoal">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-charcoal to-[#242629]" />
        <div className="container-rv relative py-20 lg:py-24">
          <Reveal className="max-w-2xl">
            <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-silver transition-colors hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Back home
            </Link>
            <p className="mt-6 flex items-center gap-2 overline text-brand-red"><ShieldCheck className="h-4 w-4" /> Your privacy</p>
            <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.05] text-white sm:text-5xl">Privacy Policy</h1>
            <p className="mt-4 text-sm text-brand-silver">Last updated: {UPDATED}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-rv max-w-3xl py-16 lg:py-20">
          <p className="text-brand-muted">
            Your trust matters as much as your car's finish. This policy explains, in plain language, what we
            collect and why — and the choices you have.
          </p>

          <div className="mt-12 space-y-10">
            {SECTIONS.map((s) => (
              <Reveal key={s.title}>
                <div>
                  <h2 className="font-serif text-2xl font-bold text-brand-charcoal">{s.title}</h2>
                  {s.body?.map((p, i) => (
                    <p key={i} className="mt-3 leading-relaxed text-brand-muted">{p}</p>
                  ))}
                  {s.list && (
                    <ul className="mt-4 space-y-2.5">
                      {s.list.map((item, i) => (
                        <li key={i} className="flex gap-3 text-brand-muted">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-red" />
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-10 rounded-2xl border border-brand-silver/40 bg-brand-offwhite p-6">
              <p className="font-serif text-lg font-bold text-brand-charcoal">Reverence Autos</p>
              <p className="mt-2 text-sm text-brand-muted">Mobile car detailing · Ottawa, Ontario, Canada</p>
              <p className="mt-1 text-sm text-brand-muted">
                Email: <a href={`mailto:${CONTACT.email}`} className="font-medium text-brand-red hover:underline">{CONTACT.email}</a>
              </p>
              <p className="mt-1 text-sm text-brand-muted">
                Phone: <a href={`tel:${CONTACT.phone}`} className="font-medium text-brand-red hover:underline">{CONTACT.phone}</a>
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
